import React from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from '../redux/hooks';
import { clearRegisterForm, updateField } from '../redux/slices/registrationSlice';
import RegistrationInputField from './InputField';
import { closeModal, openLogin, openRegister } from '../redux/slices/modalSlice';
import ModalWrapper from './ModalWrapper';
import { Link } from 'react-router-dom';

const Modal = styled.div`
  width: calc(100% - 20px);
  max-width: 800px;
  background: white;
  border-radius: 32px;
  box-shadow: 0 0 20px #00000050;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding: 20px 10px;
`;

const SubmitButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  height: 42px;
  background-color: ${(props) => (props.disabled ? '#aabbbb' : '#3B6255')};
  color: white;
  font-weight: bold;
  font-size: 18px;
  border: none;
  border-radius: 20px;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  margin-top: 14px;
`;

const ErrorText = styled.div`
  color: #AE0505;
  font-size: 10px;
  font-family: 'IBM Plex Mono';
  font-weight: 700;
`;

const SuccessText = styled.div`
  color: #3B6255;
  font-size: 12px;
  font-family: 'IBM Plex Mono';
  font-weight: 700;
  text-align: center;
  max-width: 500px;
  line-height: 1.4;
`;

const ErrorIcon = styled.img`
  max-height: 100%;
  max-width: 100%;
`;

const ErrorContainer = styled.div`
  margin-top: -6px;
  gap: 4px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 500px;
`;

const SuccessContainer = styled.div`
  margin-top: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const SwitchText = styled.div`
  margin-top: 20px;
  font-family: 'IBM Plex Mono';
  font-size: 14px;
  color: #000;
  cursor: pointer;
  text-decoration: underline;
  font-weight: 500;

  &:hover {
    color: #3B6255;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 12px;
  font-family: 'IBM Plex Mono';
  font-size: 12px;
  color: #000;
`;

const RegisterModal: React.FC = () => {
  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL;
  const dispatch = useDispatch();
  const { firstName, lastName, email, password, confirmPassword } = useSelector(state => state.registration);
  const [error, setError] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [agreed, setAgreed] = React.useState(false);

  const handleChange = (field: string, value: string) => {
    dispatch(updateField({ field: field as any, value }));
    if (error) setError('');
    if (successMessage) setSuccessMessage('');
  };

  const switchModal = (to: 'login' | 'register') => {
    dispatch(closeModal());
    setTimeout(() => {
      if (to === 'login') dispatch(openLogin());
      else dispatch(openRegister());
    }, 100);
  };

  const handleSubmit = async () => {
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    const trimmedConfirmPassword = confirmPassword.trim();

    const nameRegex = /^[А-Яа-яІіЇїЄєҐґ\s'-]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

    if (!trimmedFirstName) {
      setError('Вкажіть імʼя');
      return;
    }

    if (!nameRegex.test(trimmedFirstName)) {
      setError('Імʼя повинно містити лише символи українського алфавіту');
      return;
    }

    if (!trimmedLastName) {
      setError('Вкажіть прізвище');
      return;
    }

    if (!nameRegex.test(trimmedLastName)) {
      setError('Прізвище повинно містити лише символи українського алфавіту');
      return;
    }

    if (!emailRegex.test(trimmedEmail)) {
      setError('Некоректний email');
      return;
    }

    if (trimmedPassword.length < 6) {
      setError('Пароль має бути щонайменше 6 символів');
      return;
    }

    if (trimmedPassword !== trimmedConfirmPassword) {
      setError('Паролі не співпадають');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const res = await fetch(`${BASE_API_URL}/auth/signup`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: trimmedFirstName,
          second_name: trimmedLastName,
          email: trimmedEmail,
          phone: '',
          customer_password: trimmedPassword,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Помилка при реєстрації');
      } else {
        if (data.requiresEmailVerification) {
          setSuccessMessage('Реєстрація успішна! На вашу email адресу відправлено лист з посиланням для підтвердження. Перевірте пошту та підтвердіть email для завершення реєстрації.');
        } else {
          dispatch(clearRegisterForm());
          dispatch(closeModal());
          alert(`Реєстрація успішна ${trimmedEmail}`);
        }
      }
    } catch (err) {
      setError('Помилка мережі');
    } finally {
      setLoading(false);
    }
  };

  if (successMessage) {
    return (
      <ModalWrapper onClick={() => dispatch(closeModal())}>
        <Modal onClick={(e) => e.stopPropagation()}>
          <SuccessContainer>
            <SuccessText>{successMessage}</SuccessText>
          </SuccessContainer>
          <SwitchText onClick={() => switchModal('login')}>Увійти</SwitchText>
        </Modal>
      </ModalWrapper>
    );
  }

  return (
    <ModalWrapper onClick={() => dispatch(closeModal())}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <RegistrationInputField label="Ім'я" value={firstName} onChange={(v) => handleChange('firstName', v)} />
          <RegistrationInputField label="Прізвище" value={lastName} onChange={(v) => handleChange('lastName', v)} />
          <RegistrationInputField label="Email" value={email} onChange={(v) => handleChange('email', v)} />
          <RegistrationInputField label="Пароль" value={password} isPassword onChange={(v) => handleChange('password', v)} />
          <RegistrationInputField label="Повторіть пароль" value={confirmPassword} isPassword onChange={(v) => handleChange('confirmPassword', v)} />

          <CheckboxContainer>
            <input
              type="checkbox"
              id="agreement"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <label htmlFor="agreement">
              Підтверджую ознайомлення та згоду з{" "}
              <Link to="/terms-of-use" target="_blank" style={{ color: "#3B6255", textDecoration: "underline" }}>
                публічною офертою
              </Link>
            </label>
          </CheckboxContainer>

          {error && (
            <ErrorContainer>
              <ErrorIcon src={`${process.env.PUBLIC_URL}/media/Error_icon.svg`} alt="Error" />
              <ErrorText>{error}</ErrorText>
            </ErrorContainer>
          )}
          <SubmitButton disabled={loading || !agreed} onClick={handleSubmit}>
            {loading ? 'Завантаження...' : 'Зареєструватися'}
          </SubmitButton>
        </form>
        <SwitchText onClick={() => switchModal('login')}>Увійти</SwitchText>
      </Modal>
    </ModalWrapper>
  );
};

export default RegisterModal;
